const BOARD_NATIVE = Object.freeze({
  ITX: { l:170, w:170, h:6 },
  DTX: { l:203, w:170, h:6 },
  MATX:{ l:244, w:244, h:6 },
  ATX: { l:305, w:244, h:6 }
});

const PSU_NATIVE = Object.freeze({
  FLEX: { l:150, w:40.5, h:81.5 },
  SFX: { l:125, w:63.5, h:100 },
  'SFX-L': { l:125, w:63.5, h:125 },
  ATX: { l:150, w:86, h:140 }
});

function finite(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function normalizeMobo(value) {
  const raw = String(value || '').trim().toLowerCase()
    .replace(/micro[-_ ]?atx|m[-_ ]?atx/g, 'matx')
    .replace(/mini[-_ ]?itx/g, 'itx');
  if (raw.includes('matx')) return 'MATX';
  if (raw.includes('dtx')) return 'DTX';
  if (raw.includes('atx') && !raw.includes('itx')) return 'ATX';
  if (raw.includes('itx')) return 'ITX';
  return '';
}

function normalizePsu(value) {
  const raw = String(value || '').trim().toLowerCase().replace(/\s+/g, '').replace(/-/g, '_');
  if (raw.includes('sfx_l') || raw.includes('sfxl')) return 'SFX-L';
  if (raw.includes('flex')) return 'FLEX';
  if (raw === 'atx' || (raw.includes('atx') && !raw.includes('sfx'))) return 'ATX';
  if (raw.includes('sfx')) return 'SFX';
  return '';
}

function tokenList(value) {
  if(Array.isArray(value))return value.flatMap(tokenList);
  if(value===null||value===undefined||value==='')return[];
  if(typeof value==='object')return Object.values(value).flatMap(tokenList);
  return String(value).split(/[,;|/]+/).map(v=>v.trim()).filter(Boolean);
}

function normalizedKey(value){return String(value||'').toLowerCase().replace(/[^a-z0-9]+/g,'');}
function looseField(obj, aliases){
  if(!obj||typeof obj!=='object')return undefined;
  for(const alias of aliases)if(obj[alias]!==undefined&&obj[alias]!==null)return obj[alias];
  const wanted=new Set(aliases.map(normalizedKey));
  for(const [key,value] of Object.entries(obj))if(wanted.has(normalizedKey(key)))return value;
  return undefined;
}
function declaredSupportValues(caseObj, overlay, kind){
  const aliases=kind==='mobo'
    ? ['supportedMobo','moboSupport','motherboardSupport','motherboardCompatibility','motherboard support','motherboard compatibility']
    : ['supportedPSU','supportedPsu','psuSupport','powerSupply','powerSupplySupport','powerSupplyCompatibility','power supply support','power supply compatibility'];
  const sources=[caseObj,overlay,overlay?.specs,overlay?.specifications,overlay?.compatibility];
  return sources.flatMap(source=>tokenList(looseField(source,aliases)));
}

function baseLimits(caseObj) {
  return {
    maxGPU: finite(caseObj?.maxGPU),
    maxGPUH: finite(caseObj?.maxGPUHeight ?? caseObj?.maxGPUH),
    maxGPUT: finite(caseObj?.maxThick ?? caseObj?.maxGPUT),
    maxCPU: finite(caseObj?.maxCPU),
    maxAIO: finite(caseObj?.maxAIO),
    maxPSU: finite(caseObj?.maxPSU)
  };
}

function applyOverrides(base, overrides = {}) {
  const out = { ...base };
  for (const [rawKey, rawVal] of Object.entries(overrides || {})) {
    const key = rawKey === 'maxThick' ? 'maxGPUT' : rawKey;
    if (!(key in out)) continue;
    const n = Number(rawVal);
    if (Number.isFinite(n) && n >= 0) out[key] = n;
  }
  return out;
}

function pushUniqueState(states, seen, id, source, limits, meta = {}) {
  const key = [limits.maxGPU, limits.maxGPUH, limits.maxGPUT, limits.maxCPU, limits.maxAIO, limits.maxPSU].join('|');
  if (seen.has(key)) return;
  seen.add(key);
  states.push({ id, source, limits:{...limits}, ...meta });
}

function relationHintsFromRules(overlay) {
  const hints = [];
  for (const rule of overlay?.rules || []) {
    const conditions = rule?.conditions || {};
    const overrides = rule?.overrides || {};
    const conditionKeys = Object.keys(conditions).filter(k => conditions[k] !== null && conditions[k] !== undefined && conditions[k] !== '');
    const overrideKeys = Object.keys(overrides).filter(k => overrides[k] !== null && overrides[k] !== undefined && overrides[k] !== '');
    if (!conditionKeys.length && !overrideKeys.length) continue;
    hints.push({
      id: String(rule.rule_id || rule.id || `rule_${hints.length}`),
      conditionKeys,
      overrideKeys: overrideKeys.map(k => k === 'maxThick' ? 'maxGPUT' : k),
      conditions:{...conditions},
      overrides:{...overrides}
    });
  }
  return hints;
}

function modeSemanticHints(overlay) {
  const hints = [];
  for (const mode of overlay?.modes || []) {
    const text = `${mode?.mode_name || ''} ${mode?.name || ''} ${mode?.mode_id || ''}`.toLowerCase();
    hints.push({
      id: String(mode?.mode_id || mode?.id || ''),
      vertical: /\bvertical\b/.test(text),
      inverted: /\b(flipped|inverted|inverse)\b/.test(text),
      classic: /\bclassic\b/.test(text),
      sandwich: /\bsandwich\b/.test(text),
      console: /\bconsole\b/.test(text),
      reference: /\b(reference|classic)\b/.test(text)
    });
  }
  return hints;
}

export function buildCaseCapabilityProfile(caseObj, overlay = null) {
  const base = baseLimits(caseObj);
  const states = [];
  const seen = new Set();
  const actualModes = (Array.isArray(overlay?.modes) ? overlay.modes : []).filter(mode => mode && typeof mode === 'object' && String(mode?.mode_id ?? mode?.id ?? '').trim());
  // `base` is a case envelope, not a user-selectable mode. When structured modes exist,
  // their states are the real alternatives; adding a synthetic base state can invent an
  // impossible combination of maxima across mutually exclusive modes.
  if (!actualModes.length) pushUniqueState(states, seen, 'base', 'base', base);

  for (const mode of actualModes) {
    const limits = applyOverrides(base, mode?.overrides || {});
    pushUniqueState(states, seen, `mode:${mode?.mode_id || mode?.id || states.length}`, 'mode', limits, {
      modeId: String(mode?.mode_id || mode?.id || ''),
      modeName: String(mode?.mode_name || mode?.name || '')
    });

    const localSteps = Array.isArray(mode?.fixedSteps) ? mode.fixedSteps : [];
    for (const step of localSteps) {
      const stepLimits = applyOverrides(limits, {
        maxCPU: step?.maxCPU,
        maxGPUT: step?.maxGPUT ?? step?.maxThick
      });
      pushUniqueState(states, seen, `mode-step:${mode?.mode_id || mode?.id || 'mode'}:${step?.slotName || step?.id || states.length}`, 'mode_fixed_step', stepLimits, {
        modeId: String(mode?.mode_id || mode?.id || ''),
        stepName: String(step?.slotName || step?.id || '')
      });
    }
  }

  for (const step of overlay?.fixedSteps || []) {
    const limits = applyOverrides(base, {
      maxCPU: step?.maxCPU,
      maxGPUT: step?.maxGPUT ?? step?.maxThick
    });
    pushUniqueState(states, seen, `step:${step?.slotName || step?.id || states.length}`, 'fixed_step', limits, {
      stepName: String(step?.slotName || step?.id || '')
    });
  }

  // Support declarations can arrive from either the compact catalog record or the structured
  // overlay. Merge only explicit root/spec declarations; mode-specific restrictions still live
  // in configuration rules and are not flattened into global support.
  const rawMotherboardTypes = [...new Set(declaredSupportValues(caseObj,overlay,'mobo').map(normalizeMobo).filter(Boolean))];
  const rawPsuTypes = [...new Set(declaredSupportValues(caseObj,overlay,'psu').map(normalizePsu).filter(Boolean))];
  const motherboardSupportKnown = rawMotherboardTypes.length > 0;
  const psuSupportKnown = rawPsuTypes.length > 0;
  const motherboardTypes = rawMotherboardTypes.length ? rawMotherboardTypes : ['ITX'];
  const psuTypes = rawPsuTypes.length ? rawPsuTypes : ['SFX'];

  const gpuEnvelopes = states
    .filter(s => s.limits.maxGPU > 0 || s.limits.maxGPUT > 0 || s.limits.maxGPUH > 0)
    .map(s => ({ id:s.id, l:s.limits.maxGPU, t:s.limits.maxGPUT, h:s.limits.maxGPUH }));
  const cpuEnvelopes = states.filter(s => s.limits.maxCPU > 0).map(s => ({ id:s.id, h:s.limits.maxCPU }));
  const radiatorSizes = [...new Set(states.map(s => s.limits.maxAIO).filter(v => v > 0))].sort((a,b)=>a-b);
  const widthAllocationPairs = states
    .filter(s => s.limits.maxGPUT > 0 && s.limits.maxCPU > 0)
    .map(s => ({ id:s.id, gpuT:s.limits.maxGPUT, cpuH:s.limits.maxCPU, sum:s.limits.maxGPUT+s.limits.maxCPU }));

  const ml = overlay?.onnx_ml_features || {};
  const deterministicLayoutEvidence = {
    sandwich: !!ml.is_sandwich_layout,
    reference: !!ml.is_reference_layout,
    console: !!ml.is_console_layout,
    dynamicSpine: !!(overlay?.hasSpine || ml.has_dynamic_spine)
  };

  return {
    schemaVersion:1,
    source:'full_case_capability_pool',
    states,
    motherboardTypes,
    motherboardSupportKnown,
    motherboardEnvelopes: motherboardTypes.map(type => ({ type, dims:{...BOARD_NATIVE[type]} })),
    psuTypes,
    psuSupportKnown,
    psuEnvelopes: psuTypes.map(type => ({ type, dims:{...PSU_NATIVE[type]} })),
    gpuEnvelopes,
    cpuEnvelopes,
    radiatorSizes,
    widthAllocationPairs,
    relationHints: relationHintsFromRules(overlay),
    modeSemanticHints: modeSemanticHints(overlay),
    deterministicLayoutEvidence
  };
}
